/**
 * 비용 정산 계산 유틸리티
 *
 * 여러 사람이 각각 지출한 비용을 분석하여
 * 최적화된 정산 결과를 계산합니다.
 */

export interface PaymentData {
    paymentId: string;
    storeName: string;
    totalAmount: number;
    paidByNo?: number | null;          // 멤버 결제자
    paidByExternalNo?: number | null;  // 외부 인원 결제자
    paidByNickname: string;
    splits: {
        userNo?: number | null;
        externalNo?: number | null;
        nickname: string;
        amount: number;
        isPaid: boolean;
    }[];
}

export interface UserBalance {
    userNo: number;          // 내부 식별용: 멤버면 userNo, 외부면 -externalNo (음수로 표시)
    externalNo?: number;     // 외부인원 원본 번호
    isExternal?: boolean;
    nickname: string;
    paid: number;           // 지불한 총액
    shouldPay: number;      // 내야 할 총액
    balance: number;        // 차액 (양수: 받아야 함, 음수: 내야 함)
}

export interface Transaction {
    from: string;           // 보내는 사람
    fromUserNo: number;
    fromIsExternal?: boolean;
    to: string;             // 받는 사람
    toUserNo: number;
    toIsExternal?: boolean;
    amount: number;         // 금액
}

export interface SettlementResult {
    totalAmount: number;                    // 총 지출액
    participantCount: number;               // 참여자 수
    perPersonAmount: number;                // 1인당 평균 금액
    balances: UserBalance[];                // 참여자별 수지
    transactions: Transaction[];            // 최적화된 정산 내역
    expenses: {                             // 지출 내역
        description: string;
        amount: number;
        paidBy: string;
        splitAmong: string[];               // 지불대상자 목록
    }[];
}

// 외부 인원을 내부 키로 인코딩: externalNo → 매우 큰 음수 (guest_user 등과 충돌 방지)
const EXTERNAL_KEY_OFFSET = -1_000_000_000;
const encodeKey = (userNo?: number | null, externalNo?: number | null): number | null => {
    if (userNo != null && userNo !== 0) return userNo;
    if (externalNo != null) return EXTERNAL_KEY_OFFSET - externalNo;
    return null;
};
const isExternalKey = (key: number) => key <= EXTERNAL_KEY_OFFSET;
const decodeExternalNo = (key: number) => EXTERNAL_KEY_OFFSET - key;

/**
 * 정산 결과 계산
 */
export function calculateSettlement(payments: PaymentData[]): SettlementResult {
    if (!payments || payments.length === 0) {
        return {
            totalAmount: 0,
            participantCount: 0,
            perPersonAmount: 0,
            balances: [],
            transactions: [],
            expenses: []
        };
    }

    // 1. 전체 참여자 목록 추출 (멤버+외부 혼합, 내부 키는 encodeKey)
    const userMap = new Map<number, { key: number; nickname: string; isExternal: boolean }>();
    const balanceMap = new Map<number, { paid: number; shouldPay: number }>();

    payments.forEach(payment => {
        const payerKey = encodeKey(payment.paidByNo, payment.paidByExternalNo);
        if (payerKey !== null) {
            if (!userMap.has(payerKey)) {
                userMap.set(payerKey, {
                    key: payerKey,
                    nickname: payment.paidByNickname || String(payerKey),
                    isExternal: isExternalKey(payerKey),
                });
                balanceMap.set(payerKey, { paid: 0, shouldPay: 0 });
            }
            const payerBalance = balanceMap.get(payerKey)!;
            payerBalance.paid += payment.totalAmount;
        }

        // 분담해야 할 사람들 추가
        payment.splits.forEach(split => {
            const splitKey = encodeKey(split.userNo, split.externalNo);
            if (splitKey === null) return;
            if (!userMap.has(splitKey)) {
                userMap.set(splitKey, {
                    key: splitKey,
                    nickname: split.nickname,
                    isExternal: isExternalKey(splitKey),
                });
                balanceMap.set(splitKey, { paid: 0, shouldPay: 0 });
            }
            const userBalance = balanceMap.get(splitKey)!;
            userBalance.shouldPay += split.amount;
        });
    });

    // 2. 총액 및 평균 계산
    const totalAmount = payments.reduce((sum, p) => sum + p.totalAmount, 0);
    const participantCount = userMap.size;
    const perPersonAmount = participantCount > 0 ? Math.round(totalAmount / participantCount) : 0;

    // 3. 참여자별 수지 계산
    const balances: UserBalance[] = Array.from(userMap.values()).map(user => {
        const balance = balanceMap.get(user.key)!;
        return {
            userNo: user.key,
            externalNo: user.isExternal ? decodeExternalNo(user.key) : undefined,
            isExternal: user.isExternal,
            nickname: user.nickname,
            paid: balance.paid,
            shouldPay: balance.shouldPay,
            balance: balance.paid - balance.shouldPay
        };
    }).sort((a, b) => b.balance - a.balance); // 받아야 할 금액이 큰 순서로 정렬

    // 4. 최적화된 정산 계산 (Greedy Algorithm)
    const transactions = optimizeTransactions(balances);

    // 5. 지출 내역
    const expenses = payments.map(payment => ({
        description: payment.storeName,
        amount: payment.totalAmount,
        paidBy: payment.paidByNickname || String(payment.paidByNo),
        splitAmong: payment.splits?.map(s => s.nickname) || []
    }));

    return {
        totalAmount,
        participantCount,
        perPersonAmount,
        balances,
        transactions,
        expenses
    };
}

/**
 * 최적화된 거래 내역 계산
 * Greedy Algorithm을 사용하여 최소 거래 횟수로 정산
 */
function optimizeTransactions(balances: UserBalance[]): Transaction[] {
    const transactions: Transaction[] = [];

    // 수지 배열 복사 (원본 데이터 보존)
    const creditors = balances
        .filter(b => b.balance > 0)
        .map(b => ({ ...b }))
        .sort((a, b) => b.balance - a.balance);  // 받아야 할 금액이 큰 순

    const debtors = balances
        .filter(b => b.balance < 0)
        .map(b => ({ ...b, balance: Math.abs(b.balance) }))
        .sort((a, b) => b.balance - a.balance);  // 내야 할 금액이 큰 순

    let i = 0; // creditors 인덱스
    let j = 0; // debtors 인덱스

    while (i < creditors.length && j < debtors.length) {
        const creditor = creditors[i];
        const debtor = debtors[j];

        // 거래 금액 결정 (둘 중 작은 금액)
        const amount = Math.min(creditor.balance, debtor.balance);

        if (amount > 0) {
            transactions.push({
                from: debtor.nickname,
                fromUserNo: debtor.userNo,
                fromIsExternal: debtor.isExternal,
                to: creditor.nickname,
                toUserNo: creditor.userNo,
                toIsExternal: creditor.isExternal,
                amount: Math.round(amount)
            });
        }

        // 잔액 업데이트
        creditor.balance -= amount;
        debtor.balance -= amount;

        // 정산이 완료된 사람은 다음으로 이동
        if (creditor.balance <= 0) i++;
        if (debtor.balance <= 0) j++;
    }

    return transactions;
}

/**
 * 특정 사용자의 정산 상태 조회
 */
export function getUserSettlementStatus(
    userNo: number,
    settlement: SettlementResult
): {
    shouldReceive: Transaction[];  // 받을 금액
    shouldPay: Transaction[];      // 낼 금액
    balance: number;               // 수지
} {
    const shouldReceive = settlement.transactions.filter(t => t.toUserNo === userNo);
    const shouldPay = settlement.transactions.filter(t => t.fromUserNo === userNo);

    const userBalance = settlement.balances.find(b => b.userNo === userNo);
    const balance = userBalance?.balance || 0;

    return {
        shouldReceive,
        shouldPay,
        balance
    };
}
